import { css, cx } from '@linaria/core'
import { type FC, Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '#components/Button'
import { Dropdown } from '#components/Dropdown'
import { Icon } from '#components/Icon'
import { IconButton } from '#components/IconButton'
import ArrowLeftIcon from '#icons/arrow-left-24.svg'
import ArrowRightIcon from '#icons/arrow-right-24.svg'
import CalendarIcon from '#icons/calendar-24.svg'
import { fragments, style } from '#styles'
import {
  type DateString,
  formatPartPeriod,
  isValidDateString,
  isValidPeriod,
  type PartPeriod,
  periodToDate,
  stringToPeriod,
} from '#utils/DateString.ts'
import { useDateFormatter } from '#utils/formatDateTime'
import { partPeriodInputClass } from './style'

interface PartPeriodInputProps {
  ref?: React.Ref<HTMLDivElement>
  className?: string
  value?: PartPeriod | undefined
  valueAsDateString?: DateString
  onChange?: (value: PartPeriod | undefined) => void
  disabled?: boolean
}

export const PartPeriodInput: FC<PartPeriodInputProps> = ({ ref, value, className, valueAsDateString, onChange, disabled }) => {
  const { t } = useTranslation('shared')

  const [open, setOpen] = useState(false)
  const usedValue = value ?? (isValidDateString(valueAsDateString) ? stringToPeriod(valueAsDateString) : undefined)

  return (
    <Dropdown
      className={cx(partPeriodInputClass, className)}
      ref={ref}
      onClick={() => setOpen(true)}
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
      dropdownContent={open && <PartPeriodDropdownContent activePartPeriod={usedValue} onSelect={onChange} />}
    >
      <span className="_period">{isValidPeriod(usedValue) ? formatPartPeriod(usedValue) : t('input.part-period.placeholder', 'YYYY.MM.P')}</span>
      <Icon className="_icon" iconUrl={CalendarIcon} />
    </Dropdown>
  )
}

const partPeriodsDropdownClass = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  padding-top: 8px;
  
  > .year-selector {
    display: flex; 
    align-items: center;
    gap: 35px;
    ${fragments.textStyle.body.m.bold};
  }
  
  > .part-periods-grid {
    display: grid;
    grid-template-columns: min-content repeat(4, 1fr);
    padding: 8px;
    gap: 0 4px;
    
    > .month-name {
      display: flex;
      align-items: center;
      color: ${style.theme.textSoft};
      font-weight: 200;
      text-transform: uppercase;
      padding-right: 8px;
    }
    
    > ._period {
      font-weight: 400;
    }
    
    > ._quarter-divider {
      grid-column: 1 / -1;
      height: 1px;
      background-color: ${style.theme.borderSurface};
      margin: 4px 0;
    }
  }
`

const shortenPartPeriodLabel = true

const PartPeriodDropdownContent: FC<{
  activePartPeriod: PartPeriod | undefined
  onSelect?: (partPeriod: PartPeriod) => void
}> = ({ activePartPeriod, onSelect }) => {
  const currentYear = activePartPeriod ? periodToDate(activePartPeriod).getFullYear() : new Date().getFullYear()
  const [year, setYear] = useState(currentYear)
  const f = useDateFormatter()

  const partPeriodsByMonth: { periods: PartPeriod[]; month: { label: string; index: number } }[] = useMemo(() => {
    const periods: { periods: PartPeriod[]; month: { label: string; index: number } }[] = []

    for (let month = 1; month <= 12; month++) {
      const monthPeriods: PartPeriod[] = []
      for (let period = 1; period <= 4; period++) {
        monthPeriods.push(`${year}${month.toString().padStart(2, '0')}${period}` as PartPeriod)
      }
      periods.push({
        periods: monthPeriods,
        month: {
          label: f.shortMonth(new Date(year, month - 1)),
          index: month - 1,
        },
      })
    }
    return periods
  }, [year, f])

  return (
    <div className={partPeriodsDropdownClass}>
      <div className="year-selector">
        <IconButton iconUrl={ArrowLeftIcon} onClick={() => setYear(year - 1)} title="" />
        <span>{year}</span>
        <IconButton iconUrl={ArrowRightIcon} onClick={() => setYear(year + 1)} title="" />
      </div>
      <div className="part-periods-grid">
        {partPeriodsByMonth.map(({ periods, month }) => (
          <Fragment key={month.index}>
            <span className="month-name">{month.label}</span>
            {periods.map((period) => (
              <Button
                kind={activePartPeriod === period ? 'accent' : 'plain'}
                key={period}
                className={cx('_period', activePartPeriod === period && '_active')}
                size="s"
                onClick={() => onSelect?.(period)}
              >
                {formatPartPeriod(period, { excludeYear: shortenPartPeriodLabel })}
              </Button>
            ))}
            {month.index % 3 === 2 && month.index < 11 && <div className="_quarter-divider" />}
          </Fragment>
        ))}
      </div>
    </div>
  )
}
